import React from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import { attentionOutsideGates, type WorkspaceOperations } from './workspaceDashboard.model';
import {
  OPERATIONAL_QUEUES,
  OPERATIONAL_STATES,
  matchesOperationalFilter,
  type OperationalFilter,
} from './operationalState';

function money(value: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
}

const TONE: Record<string, 'danger' | 'warning' | 'primary'> = {
  danger: 'danger',
  warning: 'warning',
  primary: 'primary',
};

function filterLabel(filter: OperationalFilter): string {
  if (filter.kind === 'queue') return OPERATIONAL_QUEUES.find((queue) => queue.id === filter.id)?.label || '';
  if (filter.kind === 'state') return OPERATIONAL_STATES[filter.id].short;
  return 'All active';
}

function sameFilter(a: OperationalFilter, b: OperationalFilter) {
  if (a.kind === 'all' || b.kind === 'all') return a.kind === b.kind;
  return a.kind === b.kind && a.id === b.id;
}

const WorkspaceDashboardToday: React.FC<{
  ops: WorkspaceOperations;
  filter: OperationalFilter;
  onFilter: (filter: OperationalFilter) => void;
}> = ({ ops, filter, onFilter }) => {
  const attention = attentionOutsideGates(ops).filter((row) => matchesOperationalFilter(filter, row));
  const projects = ops.board.filter((card) => matchesOperationalFilter(filter, card));
  const total = ops.activeCount || 1;
  const toggle = (next: OperationalFilter) => onFilter(sameFilter(filter, next) ? { kind: 'all' } : next);

  return (
    <Stack gap={2}>
      <Box>
        <Typography level="title-lg">Operational view</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
          Derived from quotes, invoices, orders, production dates and bookings. Active workspaces only.
          The Project Workflow checklist is unchanged. Click a queue or state to filter.
        </Typography>
        <Box sx={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', bgcolor: 'neutral.200', mb: 1 }}>
          {OPERATIONAL_QUEUES.map((queue) => {
            const count = ops.queueCounts[queue.id];
            if (!count) return null;
            const lit = filter.kind === 'all'
              || (filter.kind === 'queue' && filter.id === queue.id)
              || (filter.kind === 'state' && OPERATIONAL_STATES[filter.id].queue === queue.id);
            return (
              <Box
                key={queue.id}
                sx={{
                  width: `${(count / total) * 100}%`,
                  bgcolor: lit ? 'primary.400' : 'neutral.400',
                  minWidth: 4,
                }}
              />
            );
          })}
        </Box>
        <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip
            size="sm"
            variant={filter.kind === 'all' ? 'solid' : 'outlined'}
            onClick={() => onFilter({ kind: 'all' })}
          >
            Active book {ops.activeCount}
          </Chip>
          {ops.exceptionCount > 0 && (
            <Chip size="sm" color="danger" variant="soft">
              {ops.exceptionCount} booking exception{ops.exceptionCount === 1 ? '' : 's'}
            </Chip>
          )}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' },
            gap: 1,
          }}
        >
          {OPERATIONAL_QUEUES.map((queue) => {
            const queueSelected = filter.kind === 'queue' && filter.id === queue.id;
            return (
              <Card key={queue.id} variant={queueSelected ? 'soft' : 'outlined'} size="sm">
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="baseline"
                    sx={{ cursor: 'pointer', mb: 0.5 }}
                    onClick={() => toggle({ kind: 'queue', id: queue.id })}
                  >
                    <Typography level="title-sm">{queue.label}</Typography>
                    <Typography level="title-sm">{ops.queueCounts[queue.id]}</Typography>
                  </Stack>
                  <Stack gap={0.5}>
                    {queue.states.map((stateId) => {
                      const meta = OPERATIONAL_STATES[stateId];
                      const count = ops.stateCounts[stateId];
                      const selected = filter.kind === 'state' && filter.id === stateId;
                      const alarming = meta.exception && count > 0;
                      return (
                        <Chip
                          key={stateId}
                          size="sm"
                          variant={selected ? 'solid' : alarming ? 'soft' : 'plain'}
                          color={alarming ? 'danger' : selected ? 'primary' : 'neutral'}
                          onClick={() => toggle({ kind: 'state', id: stateId })}
                          sx={{ justifyContent: 'space-between', width: '100%', '& .MuiChip-label': { width: '100%' } }}
                        >
                          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ width: '100%' }}>
                            <span>{meta.short}</span>
                            <span>{count}</span>
                          </Stack>
                        </Chip>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>

      {filter.kind !== 'all' && (
        <Box>
          <Typography level="title-lg">{filterLabel(filter)} · {projects.length}</Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
            Every active workspace in this view. Opening one goes to its workspace.
          </Typography>
          {projects.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No workspaces here.</Typography>
          ) : (
            <Stack gap={0.5}>
              {projects.map((card) => (
                <Stack
                  key={card.projectId}
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={{ xs: 0.25, sm: 1 }}
                  alignItems={{ sm: 'center' }}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}
                >
                  <Typography
                    level="body-sm"
                    fontWeight="lg"
                    component={Link}
                    to={card.href}
                    sx={{ textDecoration: 'none', minWidth: { sm: 220 } }}
                  >
                    {card.workspace}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600', minWidth: { sm: 160 } }}>
                    {card.customer}
                  </Typography>
                  <Chip size="sm" variant="soft" color={card.exception ? 'danger' : 'neutral'}>
                    {card.stateLabel}
                  </Chip>
                  {card.flags.map((flag) => (
                    <Chip key={flag} size="sm" variant="outlined" color="warning">{flag}</Chip>
                  ))}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Box>
        <Typography level="title-lg">Needs attention</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
          One reason per workspace, booking exceptions first. Rows already in a card above are left out. Open goes to the tab that can clear it.
        </Typography>
        {attention.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Nothing flagged in this view.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& th': { textAlign: 'left', fontSize: '0.75rem', color: 'neutral.500', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' },
                '& td': { py: 1, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'middle' },
              }}
            >
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Customer</th>
                  <th>Why it is here</th>
                  <th>Age</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {attention.map((row) => (
                  <tr key={row.projectId}>
                    <td>
                      <Typography
                        level="body-sm"
                        fontWeight="lg"
                        component={Link}
                        to={row.href}
                        sx={{ textDecoration: 'none' }}
                      >
                        {row.workspace}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{row.customer}</Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft" color={TONE[row.tone] || 'neutral'}>
                        {row.why}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-sm">{row.ageLabel}</Typography>
                    </td>
                    <td>
                      <Button size="sm" variant="plain" component={Link} to={row.href}>
                        {row.next}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>Money sitting still</Typography>
            <Stack gap={0.75}>
              {(ops.money.length ? ops.money : [{ label: 'No issued unpaid cash', amount: 0, count: 0 }]).map((row) => (
                <Stack key={row.label} direction="row" justifyContent="space-between" gap={1}>
                  <Typography level="body-sm">{row.label}</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {row.count ? `${money(row.amount)} · ${row.count}` : money(0)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>Waiting on us vs them</Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Office</Typography>
            {ops.officeReasons.length === 0 && (
              <Typography level="body-sm">Nothing waiting on the office.</Typography>
            )}
            {ops.officeReasons.map((row) => (
              <Typography key={row.label} level="body-sm">{row.label} · {row.count}</Typography>
            ))}
            <Divider sx={{ my: 1 }} />
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Customer</Typography>
            {ops.customerReasons.length === 0 && (
              <Typography level="body-sm">Nothing waiting on a customer.</Typography>
            )}
            {ops.customerReasons.map((row) => (
              <Typography key={row.label} level="body-sm">{row.label} · {row.count}</Typography>
            ))}
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
};

export default WorkspaceDashboardToday;
